import React, { useState, useEffect, useRef } from 'react';
import 'swiper/css';
import ProductInfoModal from './component/ProductInfoModal';
import ProductCautionModal from './component/ProductCautionModal';
import POSCategoriesSlider from './component/POSCategoriesSlider';
import POSSidebarCard from './component/POSSidebarCard';
import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';
import SearchForm from './component/SearchForm';
import POSReceiptModal from './component/POSReceiptModal';
import { useNavigate, useParams } from 'react-router-dom';
import validationErrors from '@/lib/validationErrors';
import { getTableBySlugDiningTableService } from '@/services/DiningTableServices';
import { getTableCategoryService } from '@/services/ItemCategoryServices';
import { getTableItemService } from '@/services/ItemServices';
import { getGeneralSettingStoreAfterLoginService } from '@/services/AuthServices';
import { useSelector, useDispatch } from 'react-redux';
import { getFileUrl } from '@/services/CommonService';
import { updateOrderTable, removeOrderTable } from '@/store/actions';
import { cn } from '@/lib/utils';
import ConfirmOrderModal from '../../../components/ui/modals/ConfirmOrderModal';
import { GeneralSettingList } from '@/constants/GeneralSettingConstant';
import { setBranchId, setCompanyId } from '@/security/Axios';
import Moment from 'moment';
import axios from 'axios';

const TablePosPage = () => {
  const { generalSetting, todayDateUpdate } = useSelector((state) => state.GeneralSetting);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [receiptOrderId, setReceiptOrderId] = useState('');
  const [categoriesSelectedValue, setCategoriesSelectedValue] = useState(0);
  const [itemSearchValue, setItemSearchValue] = useState('');
  const [dataCategorySource, setDataCategorySource] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [confirmOrderModalOpen, setConfirmOrderModalOpen] = useState(false);
  const [selectProductData, setSelectProductData] = useState({});
  const [productInfoModalOpen, setProductInfoModalOpen] = useState(false);
  const [selectProductCautionData, setSelectProductCautionData] = useState({});
  const [productCautionModalOpen, setProductCautionModalOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [foodType, setFoodType] = useState();
  const [productLayout, setProductLayout] = useState('list');
  const [orderNo, setOrderNo] = useState(null);

  // keep previous tenant headers to restore on unmount
  const prevBranchRef = useRef(null);
  const prevCompanyRef = useRef(null);

  useEffect(() => {
    setReceiptOrderId('');
  }, []);

  useEffect(() => {
    const handleUnload = () => {
      try {
        localStorage.removeItem('tableOrder');
      } catch {}
      dispatch(removeOrderTable());
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      // Only keep cart while staying inside the table flow
      const nextPath = window.location.pathname || '';
      const stayingInMenu = nextPath.startsWith('/menu/');
      if (!stayingInMenu) {
        try {
          localStorage.removeItem('tableOrder');
        } catch {}
        dispatch(removeOrderTable());
      }
    };
  }, [dispatch]);

  // remember & restore tenant headers when leaving this page
  // ✅ Safer: only restore if we are leaving the customer menu namespace
  useEffect(() => {
    prevBranchRef.current = axios.defaults.headers.common['X-Branch-Id'] ?? null;
    prevCompanyRef.current = axios.defaults.headers.common['X-Company-Id'] ?? null;

    return () => {
      const nextPath = window.location.pathname || '';
      const stayingInMenu = nextPath.startsWith('/menu/');
      if (stayingInMenu) return; // keep table’s tenant headers for checkout/payment

      // otherwise restore (going back to backoffice/admin etc.)
      if (prevBranchRef.current == null) setBranchId(undefined);
      else setBranchId(String(prevBranchRef.current));
      if (prevCompanyRef.current == null) setCompanyId(undefined);
      else setCompanyId(String(prevCompanyRef.current));
    };
  }, []);

  useEffect(() => {
    if (slug) {
      getDataBySlug(slug);
    }
  }, [slug]); // keep deps clean

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const extractedOrderNo = searchParams.get('order_no');
    setOrderNo(extractedOrderNo);
    if (extractedOrderNo) setConfirmOrderModalOpen(true);

    const hasAdminToken =
      !!localStorage.getItem('adminToken') || !!localStorage.getItem('jwt_token');
    if (hasAdminToken && todayDateUpdate !== Moment.utc().format('DD/MM/YYYY')) {
      dispatch(getGeneralSettingStoreAfterLoginService({ key_list: GeneralSettingList })).catch(
        () => {}
      );
    }
  }, [dispatch, todayDateUpdate]);

  const foodTypeOnchangeFilter = (name) => {
    if (name === 'veg') {
      setFoodType((prev) => (prev === 1 ? undefined : 1));
    } else if (name === 'non-veg') {
      setFoodType((prev) => (prev === 2 ? undefined : 2));
    }
  };

  const onSelectProductData = (item) => {
    setProductInfoModalOpen(true);
    setSelectProductData(item);
  };

  const onSelectProductCauction = (item) => {
    setProductCautionModalOpen(true);
    setSelectProductCautionData(item);
  };

  const getDataBySlug = (slugVal) => {
    setIsLoading(true);
    dispatch(getTableBySlugDiningTableService(slugVal))
      .then((res) => {
        const table = res?.data?.data;
        if (table) {
          // 🔐 Lock axios/global tenant headers to the table's tenant
          const bId = String(table?.branch_id?._id || table?.branch_id || '');
          const cId = String(table?.company_id?._id || table?.company_id || '');
          if (cId) setCompanyId(cId);
          if (bId) setBranchId(bId);

          // fetch lists afterwards (respecting headers) and client-filter as a failsafe
          getCategoryData();
          getItemData(bId, cId);

          dispatch(
            updateOrderTable({
              order_type: 2,
              service_mode: 1,
              dining_table_id: table._id,
              user_id: generalSetting?.default_customer_id,
              branch_id: bId || undefined,
            })
          );
        } else {
          navigate('/');
        }
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        navigate('/');
      })
      .finally(() => setIsLoading(false));
  };

  const getCategoryData = () => {
    setIsLoading(true);
    dispatch(getTableCategoryService())
      .then((res) => {
        setDataCategorySource(res?.data?.data || []);
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  // Fetch items; keep only those from the table's branch as a failsafe
  const getItemData = (branchIdFromTable, companyIdFromTable) => {
    setIsLoading(true);
    dispatch(
      getTableItemService({
        branch_id: branchIdFromTable ? String(branchIdFromTable) : undefined,
        company_id: companyIdFromTable ? String(companyIdFromTable) : undefined,
        in_stock_only: false,
      })
    )
      .then((res) => {
        const items = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.data?.data)
          ? res.data.data.data
          : [];

        const bId = String(branchIdFromTable || '');
        const filtered = bId
          ? items.filter(
              (i) => String(i?.branch_id?._id || i?.branch_id || '') === bId
            )
          : items;

        setDataSource(filtered);
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const addTableOrder = () => {
    navigate(`/menu/checkout/${slug}`);
  };

  return (
    <>
      <section className="mb-16 mt-4">
        <div className="container">
          <SearchForm itemSearchValue={itemSearchValue} setItemSearchValue={setItemSearchValue} />
          <POSCategoriesSlider
            dataCategorySource={dataCategorySource}
            categoriesSelectedValue={categoriesSelectedValue}
            setCategoriesSelectedValue={setCategoriesSelectedValue}
            disabled={isLoading}
          />

          {/* tiny loading hint just to consume isLoading so ESLint is happy */}
          {isLoading && (
            <div className="text-xs text-[#A0A3BD] mb-2" role="status" aria-live="polite">
              Loading…
            </div>
          )}

          <div className="flex flex-wrap gap-3 w-full mb-5 veg-navs">
            <button
              className={cn(
                'flex items-center gap-3 w-fit pl-3 pr-4 py-1.5 rounded-3xl transition hover:shadow-filter hover:bg-white bg-[#EFF0F6]',
                foodType === 2 && 'veg-active',
                isLoading && 'opacity-60 pointer-events-none'
              )}
              type="button"
              onClick={() => foodTypeOnchangeFilter('non-veg')}
              disabled={isLoading}
            >
              <img src="/assets/images/item-type/non-veg.png" alt="category" className="h-6" />
              <span className="capitalize text-sm font-medium text-heading">Non-Veg</span>
            </button>
            <button
              className={cn(
                'flex items-center gap-3 w-fit pl-3 pr-4 py-1.5 rounded-3xl transition hover:shadow-filter hover:bg-white bg-[#EFF0F6]',
                foodType === 1 && 'veg-active',
                isLoading && 'opacity-60 pointer-events-none'
              )}
              type="button"
              onClick={() => foodTypeOnchangeFilter('veg')}
              disabled={isLoading}
            >
              <img src="/assets/images/item-type/veg.png" alt="category" className="h-6" />
              <span className="capitalize text-sm font-medium text-heading">Veg</span>
            </button>
          </div>

          <div className="flex gap-4 items-center justify-between mb-6">
            <h2 className="capitalize text-[26px] leading-[40px] font-semibold text-center sm:text-left text-primary">
              All Items
            </h2>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className={cn(
                  'lab lab-row-vertical lab-font-size-20 text-xl',
                  productLayout === 'list' ? 'text-heading' : 'text-[#A0A3BD]',
                  isLoading && 'pointer-events-none opacity-60'
                )}
                onClick={() => setProductLayout('list')}
                disabled={isLoading}
              />
              <button
                type="button"
                className={cn(
                  'lab lab-element-3 lab-font-size-20 text-xl',
                  productLayout === 'grid' ? 'text-heading' : 'text-[#A0A3BD]',
                  isLoading && 'pointer-events-none opacity-60'
                )}
                onClick={() => setProductLayout('grid')}
                disabled={isLoading}
              />
            </div>
          </div>

          <div
            className={cn(
              'grid ',
              productLayout === 'list'
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6'
                : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 lg:gap-6'
            )}
          >
            {dataSource
              .filter((item) =>
                categoriesSelectedValue === 0
                  ? true
                  : item.item_category?._id === categoriesSelectedValue
              )
              .filter((item) =>
                itemSearchValue
                  ? item.name?.toLowerCase().includes(itemSearchValue.toLowerCase())
                  : true
              )
              .filter((item) => (foodType ? item.item_type === foodType : true))
              .map((item) =>
                productLayout === 'list' ? (
                  <div key={item._id} className="product-card-list overflow-hidden">
                    <img
                      className="product-card-list-image"
                      src={getFileUrl(item.file_name, 'product')}
                      alt={item.name}
                    />
                    <div className="product-card-list-content-group">
                      <div className="product-card-list-header-group">
                        <h3 className="product-card-list-title">{item.name}</h3>
                        <button className="info-btn" onClick={() => onSelectProductCauction(item)}>
                          <i className="lab lab-information font-fill-paragraph transition lab-font-size-16" />
                        </button>
                      </div>
                      <p className="product-card-list-describe char-limit">{item.description}</p>
                      <div className="product-card-list-footer-group">
                        <div className="product-card-list-price-group">
                          {item.offer?.discount_per > 0 && (
                            <del className="product-card-grid-price-previous">
                              <CurrencyCodeAdd price={item.price} />
                            </del>
                          )}
                          <h4 className="product-card-list-price-current">
                            <CurrencyCodeAdd
                              price={
                                item.price -
                                (item.price * (item.offer?.discount_per || 0)) / 100
                              }
                            />
                          </h4>
                        </div>
                        <button
                          onClick={() => onSelectProductData(item)}
                          className={cn(
                            'product-card-list-cart-btn add-btn',
                            isLoading && 'pointer-events-none opacity-60'
                          )}
                          disabled={isLoading}
                        >
                          <i className="lab lab-bag-2 font-fill-primary transition lab-font-size-14" />
                          <span className="text-xs text-primary transition">Add</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div key={item._id} className="product-card-grid">
                    <img
                      className="product-card-grid-image w-[260px] min-h-[180px] h-[180px] object-cover object-center"
                      src={getFileUrl(item.file_name, 'product')}
                      alt={item.name}
                    />
                    <div className="product-card-grid-content-group">
                      <div className="product-card-grid-header-group">
                        <h3 className="product-card-grid-title">{item.name}</h3>
                        <button
                          type="button"
                          onClick={() => onSelectProductCauction(item)}
                          className="product-card-grid-info-btn info-btn"
                        >
                          <i className="lab lab-information font-fill-paragraph transition lab-font-size-16" />
                        </button>
                      </div>
                      <p className="product-card-grid-describe char-limit">{item.description}</p>
                      <div className="product-card-grid-footer-group">
                        <div className="product-card-grid-price-group">
                          {item.offer?.discount_per > 0 && (
                            <del className="product-card-grid-price-previous">
                              <CurrencyCodeAdd price={item.price} />
                            </del>
                          )}
                          <h4 className="product-card-grid-price-current">
                            <CurrencyCodeAdd
                              price={
                                item.price -
                                (item.price * (item.offer?.discount_per || 0)) / 100
                              }
                            />
                          </h4>
                        </div>
                        <button
                          onClick={() => onSelectProductData(item)}
                          className={cn(
                            'product-card-grid-cart-btn add-btn',
                            isLoading && 'pointer-events-none opacity-60'
                          )}
                          disabled={isLoading}
                        >
                          <i className="lab lab-bag-2 font-fill-primary transition lab-font-size-14" />
                          <span className="text-xs text-primary transition">Add</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )
              )}
          </div>

          {/* Optional: Receipt modal if you use it elsewhere */}
          <POSReceiptModal
            modalOpen={receiptModalOpen}
            setModalOpen={setReceiptModalOpen}
            receiptOrderId={receiptOrderId}
          />

          <ProductInfoModal
            modalOpen={productInfoModalOpen}
            setModalOpen={setProductInfoModalOpen}
            handleProductCauction={onSelectProductCauction}
            selectProductData={selectProductData}
            setSelectProductData={setSelectProductData}
          />
          <ProductCautionModal
            modelTitle={selectProductCautionData.name}
            itemDescription={selectProductCautionData.caution}
            modalOpen={productCautionModalOpen}
            setModalOpen={setProductCautionModalOpen}
          />
        </div>
      </section>

      <POSSidebarCard addTableOrder={addTableOrder} />

      <ConfirmOrderModal
        openModel={confirmOrderModalOpen}
        handleModelClose={setConfirmOrderModalOpen}
        orderNo={orderNo}
        slug={slug}
      />
    </>
  );
};

export default TablePosPage;
